/**
 * DI registry for all block schema loaders.
 * Each layer registers its loader via setXxxLoader() at startup,
 * and renderers call getXxxLoader() at render time.
 */

import type {
  ComposeLoader,
  ComposeSchema,
  FormSchemaLoader,
  ViewSchemaLoader,
  TableSchemaLoader,
} from '../types/blocks';

type ChangeListener = () => void;

// --- Internal state ---

let _composeLoader: ComposeLoader | null = null;
let _formLoader: FormSchemaLoader | null = null;
let _viewLoader: ViewSchemaLoader | null = null;
let _tableLoader: TableSchemaLoader | null = null;

let _composeVersion = 0;
const _composeListeners = new Set<ChangeListener>();

let _formVersion = 0;
const _formListeners = new Set<ChangeListener>();

let _viewVersion = 0;
const _viewListeners = new Set<ChangeListener>();

// --- Compose loader ---

export function setComposeLoader(loader: ComposeLoader): void {
  _composeLoader = loader;
}

export function getComposeLoader(): ComposeLoader | null {
  return _composeLoader;
}

export function notifyComposeChanged(): void {
  _composeVersion++;
  _composeListeners.forEach((fn) => fn());
}

export function getComposeVersion(): number {
  return _composeVersion;
}

export function onComposeChanged(listener: ChangeListener): () => void {
  _composeListeners.add(listener);
  return () => _composeListeners.delete(listener);
}

// --- Form loader ---

export function setFormLoader(loader: FormSchemaLoader): void {
  _formLoader = loader;
}

export function getFormLoader(): FormSchemaLoader | null {
  return _formLoader;
}

// --- View loader ---

export function setViewLoader(loader: ViewSchemaLoader): void {
  _viewLoader = loader;
}

export function getViewLoader(): ViewSchemaLoader | null {
  return _viewLoader;
}

// --- Form change notification ---

export function notifyFormChanged(): void {
  _formVersion++;
  _formListeners.forEach((fn) => fn());
}

export function getFormVersion(): number {
  return _formVersion;
}

export function onFormChanged(listener: ChangeListener): () => void {
  _formListeners.add(listener);
  return () => _formListeners.delete(listener);
}

// --- View change notification ---

export function notifyViewChanged(): void {
  _viewVersion++;
  _viewListeners.forEach((fn) => fn());
}

export function getViewVersion(): number {
  return _viewVersion;
}

export function onViewChanged(listener: ChangeListener): () => void {
  _viewListeners.add(listener);
  return () => _viewListeners.delete(listener);
}

// --- Table loader ---

export function setTableLoader(loader: TableSchemaLoader): void {
  _tableLoader = loader;
}

export function getTableLoader(): TableSchemaLoader | null {
  return _tableLoader;
}
